import Controller from './Controller';
import type Report from '../checks/Report';
import { DependencyLoopCheck, InheritanceLoopCheck } from '../checks/general';
import {
  NameCheck,
  ObjectAlphabetRuleCheck,
  ObjectAttributeCheck,
  DependencyNameCheck,
  OwnedMethodEventOwningCheck,
  MethodPropagationCheck,
  MPCPathsCheck,
  FSMForwardInaccessibilityCheck,
  FSMBackwardInaccessibilityCheck,
  FSMUsedEventsCheck,
  MPCDisabledPathUsageCheck,
  MPCConflictCheck,
  FSMNonDeterminismCheck,
  InheritedCreateMethodCheck,
  ObjectInheritanceCheck,
  ObjectAttributeNameConflictCheck,
  UnusedEventCheck,
} from '../checks/specific';
import {
  ObjectDuplicateCheck,
  EventDuplicateCheck,
  DependencyDuplicateCheck,
  MethodDuplicateCheck,
  AcquiredMethodDuplicateCheck,
  DataTypeDuplicateCheck,
  AttributeDuplicateCheck,
  StateDuplicateCheck,
  FSMDuplicateCheck,
  MPCDuplicateCheck,
} from '../checks/duplicates';
import {
  DependencyConsistencyCheck,
  AcquiredMethodConsistencyCheck,
  MPCConsistencyCheck,
  TransitionConsistencyCheck,
  EventConsistencyCheck,
  InheritedMethodConsistencyCheck,
  SpecialisedMethodConsistencyCheck,
} from '../checks/consistency';
import type { AnyCheck } from '../commands/types';
import type { IMetaModel } from '../../stores/model/metamodel';
import type { IObjectType } from '../../stores/model/metamodel/object_type';
import type { IEventType } from '../../stores/model/metamodel/event_type';
import type { IDependency } from '../../stores/model/metamodel/dependency';
import type { IMethod } from '../../stores/model/metamodel/method';
import type { IDataType } from '../../stores/model/metamodel/data_type';
import type { IFSM } from '../../stores/model/metamodel/fsm';
import type { IState } from '../../stores/model/metamodel/state';
import type { ITransition } from '../../stores/model/metamodel/transition';
import type { IAttribute } from '../../stores/model/metamodel/attribute';
import type { IMultiplePropagationConstraint } from '../../stores/model/metamodel/multiple_propagation_constraint';

/**
 * Controller to run a full check on the model.
 *
 * Runs all relevant checks on the model.
 */
export default class CheckModelController extends Controller {
  // Return the checks for an attribute
  _checkAttribute(metamodel: IMetaModel, attribute: IAttribute): AnyCheck[] {
    return [new AttributeDuplicateCheck(metamodel, attribute)];
  }

  // Return the checks for a state
  _checkState(metamodel: IMetaModel, state: IState): AnyCheck[] {
    return [new NameCheck(metamodel, state), new StateDuplicateCheck(metamodel, state)];
  }

  // Return the checks for a transition
  _checkTransition(metamodel: IMetaModel, transition: ITransition): AnyCheck[] {
    return [
      new NameCheck(metamodel, transition),
      new TransitionConsistencyCheck(metamodel, transition),
    ];
  }

  // Return the checks for an FSM
  _checkFSM(metamodel: IMetaModel, fsm: IFSM): AnyCheck[] {
    const checks: AnyCheck[] = [
      new FSMDuplicateCheck(metamodel, fsm),
      new FSMForwardInaccessibilityCheck(metamodel, fsm),
      new FSMBackwardInaccessibilityCheck(metamodel, fsm),
      new FSMNonDeterminismCheck(metamodel, fsm),
      new FSMUsedEventsCheck(metamodel, fsm),
    ];
    if (fsm.type !== 'DEFAULT') {
      checks.push(new NameCheck(metamodel, fsm));
    }
    fsm.transitions.forEach((transition) => {
      checks.push(...this._checkTransition(metamodel, transition));
    });
    fsm.states.forEach((state) => {
      checks.push(...this._checkState(metamodel, state));
    });
    return checks;
  }

  // Return the checks for a multiple propagation constraint
  _checkMPC(metamodel: IMetaModel, mpc: IMultiplePropagationConstraint): AnyCheck[] {
    return [
      new MPCConsistencyCheck(metamodel, mpc),
      new MPCPathsCheck(metamodel, mpc),
      new MPCDuplicateCheck(metamodel, mpc),
      new MPCDisabledPathUsageCheck(metamodel, mpc),
      new MPCConflictCheck(metamodel, mpc),
    ];
  }

  // Return the checks for an object type
  _checkObject(metamodel: IMetaModel, object: IObjectType): AnyCheck[] {
    const checks: AnyCheck[] = [
      new NameCheck(metamodel, object),
      new ObjectDuplicateCheck(metamodel, object),
      new ObjectAlphabetRuleCheck(metamodel, object),
      new ObjectAttributeCheck(metamodel, object),
      new ObjectInheritanceCheck(metamodel, object),
    ];
    object.attributes.forEach((attribute) => {
      checks.push(...this._checkAttribute(metamodel, attribute));
      checks.push(new ObjectAttributeNameConflictCheck(metamodel, attribute));
    });
    object.multiplePropagationConstraints.forEach((mpc) => {
      checks.push(...this._checkMPC(metamodel, mpc));
    });
    object.fsms.forEach((fsm) => {
      checks.push(...this._checkFSM(metamodel, fsm));
    });
    return checks;
  }

  // Return the checks for an event
  _checkEvent(metamodel: IMetaModel, event: IEventType): AnyCheck[] {
    const checks: AnyCheck[] = [
      new NameCheck(metamodel, event),
      new EventConsistencyCheck(metamodel, event),
      new EventDuplicateCheck(metamodel, event),
      new UnusedEventCheck(metamodel, event),
    ];
    event.attributes.forEach((attribute) => {
      checks.push(...this._checkAttribute(metamodel, attribute));
    });
    return checks;
  }

  // Return the checks for a dependency
  _checkDependency(metamodel: IMetaModel, dependency: IDependency): AnyCheck[] {
    return [
      new DependencyNameCheck(metamodel, dependency),
      new DependencyDuplicateCheck(metamodel, dependency),
      new DependencyConsistencyCheck(metamodel, dependency),
    ];
  }

  // Return the checks for a method
  _checkMethod(metamodel: IMetaModel, method: IMethod): AnyCheck[] {
    const checks: AnyCheck[] = [
      new NameCheck(metamodel, method),
      new MethodDuplicateCheck(metamodel, method),
      new MethodPropagationCheck(metamodel, method),
    ];
    if (method.provenance === 'OWNED') {
      checks.push(new OwnedMethodEventOwningCheck(metamodel, method));
    } else if (method.provenance === 'ACQUIRED') {
      checks.push(
        new AcquiredMethodConsistencyCheck(metamodel, method),
        new AcquiredMethodDuplicateCheck(metamodel, method)
      );
    } else if (method.provenance === 'INHERITED') {
      checks.push(new InheritedMethodConsistencyCheck(metamodel, method));
      checks.push(new InheritedCreateMethodCheck(metamodel, method));
    } else if (method.provenance === 'SPECIALISED') {
      checks.push(new SpecialisedMethodConsistencyCheck(metamodel, method));
    }
    method.attributes.forEach((attribute) => {
      checks.push(...this._checkAttribute(metamodel, attribute));
    });
    return checks;
  }

  // Return the checks for a data type
  _checkDataType(metamodel: IMetaModel, datatype: IDataType): AnyCheck[] {
    return [new DataTypeDuplicateCheck(metamodel, datatype)];
  }

  /**
   * Check the full model
   */
  async checkModel(): Promise<Report> {
    return this.executeAndCheck((model) => {
      const checks: AnyCheck[] = [];
      const metamodel = model.metamodel;

      // Collect all checks ...
      checks.push(new DependencyLoopCheck(metamodel));
      checks.push(new InheritanceLoopCheck(metamodel));
      metamodel.objectTypes.forEach((object) => {
        checks.push(...this._checkObject(metamodel, object));
      });
      metamodel.eventTypes.forEach((event) => {
        checks.push(...this._checkEvent(metamodel, event));
      });
      metamodel.dependencies.forEach((dependency) => {
        checks.push(...this._checkDependency(metamodel, dependency));
      });
      metamodel.methods.forEach((method) => {
        checks.push(...this._checkMethod(metamodel, method));
      });
      metamodel.dataTypes.forEach((dataType) => {
        checks.push(...this._checkDataType(metamodel, dataType));
      });

      // ... and return them for executeAndCheck to actually execute them
      return checks;
    });
  }

  /**
   * Check an FSM within the model
   *
   * @param {String} id the id of the FSM to check
   */
  async checkFSM(id: string): Promise<Report> {
    return this.executeAndCheck((model) => {
      const checks: AnyCheck[] = [];
      const metamodel = model.metamodel;

      const fsm = metamodel.getFSMById(id)!;

      // Collect all checks
      checks.push(...this._checkFSM(metamodel, fsm));

      // ... and return them for executeAndCheck to actually execute them
      return checks;
    });
  }
}
